import React from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash';
import clsx from 'clsx';

import { useTheme } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Badge from '@material-ui/core/Badge';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Divider from '@material-ui/core/Divider';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import OutlinedInput from '@material-ui/core/OutlinedInput';
import Paper from '@material-ui/core/Paper';
import Skeleton from '@material-ui/lab/Skeleton';
import Typography from '@material-ui/core/Typography';

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import SearchIcon from '@material-ui/icons/Search';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';

import * as math from '_core/utils/math';
import * as api from '_core/utils/api';


export default React.memo((props) => {
  const {
    l,
    classes,
    
    getCellStyles,
    columns,
    setColumns,
    order,
    setOrder,
    filterGet,
    filterApply
  } = props;
  
  
  const theme = useTheme();
  
  const headerContainerRef = React.useRef();
  
  const [shiftColumn, setShiftColumn] = React.useState(0);
  const [shiftColumnMax, setShiftColumnMax] = React.useState(0);
  
  const [filterColumn, setFilterColumn] = React.useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = React.useState(null);
  const [filterOrder, setFilterOrder] = React.useState(null);
  const [filterValue, setFilterValue] = React.useState(null);
  
  const filterValueToggle = (val) => {
    let value = filterValue;
    
    if (!Array.isArray(value))
      value = [];
    
    if (value.indexOf(val) < 0) {
      setFilterValue([...value, val]);
    } else {
      setFilterValue(value.filter((item) => item !== val));
    }
  };
  
  React.useEffect(() => {
    const headerContainer = headerContainerRef.current;
    
    if (!headerContainer)
      return;
    
    const windowOnResize = debounce(() => {
      const width = headerContainer.offsetWidth;
      
      let offset = 0;
      
      if (columns.every((column, index) => {
            if (offset > width) {
              setShiftColumnMax(columns.length - index);
              
              return false;
            } else {
              offset += theme.spacing(column.width);
              
              return true;
            }
          })) {
        setShiftColumnMax(columns.length);
      }
    }, 50);
    
    windowOnResize();
    
    window.addEventListener('resize', windowOnResize);
    
    return () => window.removeEventListener('resize', windowOnResize);
  }, [theme, columns, headerContainerRef]);
  
  React.useEffect(() => {
    if (shiftColumn > shiftColumnMax) {
      for (let c = shiftColumnMax + 1; c <= Math.min(shiftColumn, columns.length - 1); c++)
        columns[c].currentWidth = columns[c].width;
      
      setShiftColumn(shiftColumnMax);
    }
  }, [shiftColumnMax]);
  
  
  const shiftColumns = (value) => {
    const shift = Math.min(Math.max(shiftColumn + value, 0), shiftColumnMax);
    
    if (value < 0) {
      columns[shiftColumn].currentWidth = columns[shiftColumn].width;
    } else {
      columns[shift].currentWidth = 0;
    }
    
    setShiftColumn(shift);
    setColumns([...columns]);
  };
  
  const filterBtnApply = () => {
    if (filterOrder !== order)
      setOrder(filterOrder);
    
    filterApply(filterColumn.nameSplit, filterValue);
    
    setFilterAnchorEl(null);
  };
  
  const filterBtnReset = () => {
    const columnName = filterColumn.nameSplit[0].join('.');
    
    if (order === columnName || order === '-' + columnName)
      setOrder(null);
    
    filterApply(filterColumn.nameSplit, null);
    
    setFilterAnchorEl(null);
  };
  
  return (
    <Paper elevation={0}>
      <Box display="flex">
        <div ref={headerContainerRef} className={classes.headerContainer}>
          {columns.map((column) => {
            const columnName = column.nameSplit[0].join('.');
            
            const hasBadge = order === columnName || order === '-' + columnName || !!filterGet(column.nameSplit);
            
            return (
              <div
                key={column.name}
                className={classes.headerCell}
                style={getCellStyles(theme, column, false)}
              >
                <div
                  className={classes.headerCellInner}
                  style={getCellStyles(theme, column, true)}
                >
                  <Button
                    className={classes.headerCellBtn}
                    variant="text"
                    fullWidth
                    startIcon={column.icon ? <Icon>{column.icon}</Icon> : null}
                    endIcon={(
                      <Badge color="primary" variant="dot" invisible={!hasBadge}>
                        <ExpandMoreIcon />
                      </Badge>
                    )}
                    onClick={(event) => { setFilterColumn(column); setFilterOrder(order); setFilterValue(filterGet(column.nameSplit)); setFilterAnchorEl(event.currentTarget); }}
                  >
                    {l(column.title)}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        
        <Box display="flex" alignItems="center">
          <button disabled={!shiftColumn} className={classes.headerShiftBtn} onClick={() => shiftColumns(-1)}>
            <ChevronLeftIcon fontSize="small" />
          </button>
          
          <button disabled={shiftColumn >= shiftColumnMax} className={classes.headerShiftBtn} onClick={() => shiftColumns(1)}>
            <ChevronRightIcon fontSize="small" />
          </button>
        </Box>
      </Box>
      
      <Menu
        variant="menu"
        elevation={0}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={filterAnchorEl}
        keepMounted
        open={Boolean(filterAnchorEl)}
        onClose={() => setFilterAnchorEl(null)}
      >
        {filterColumn ? (() => {
          const columnName = filterColumn.nameSplit[0].join('.');
          
          return (
            <>
              <ListItem className={classes.filterHeader}>
                <ListItemIcon>
                  {filterColumn.icon ? <Icon>{filterColumn.icon}</Icon> : null}
                </ListItemIcon>
                <ListItemText primary={l(filterColumn.title)} />
              </ListItem>
              
              <Divider />
              
              <MenuItem onClick={() => setFilterOrder(filterOrder !== columnName ? columnName : null)}>
                <ListItemIcon>
                  <ArrowUpwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={l('$backoffice.list.sort.asc')} />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge="end"
                    onChange={() => setFilterOrder(filterOrder !== columnName ? columnName : null)}
                    checked={filterOrder === columnName}
                  />
                </ListItemSecondaryAction>
              </MenuItem>
              
              <MenuItem onClick={() => setFilterOrder(filterOrder !== '-' + columnName ? '-' + columnName : null)}>
                <ListItemIcon>
                  <ArrowDownwardIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={l('$backoffice.list.sort.desc')} />
                <ListItemSecondaryAction>
                  <Checkbox
                    edge="end"
                    onChange={() => setFilterOrder(filterOrder !== '-' + columnName ? '-' + columnName : null)}
                    checked={filterOrder === '-' + columnName}
                  />
                </ListItemSecondaryAction>
              </MenuItem>
              
              <filterColumn.FilterComponent
                {...props}
                
                filterColumn={filterColumn}
                
                filterValue={filterValue}
                filterValueToggle={filterValueToggle}
                setFilterValue={setFilterValue}
                
                filterApply={filterBtnApply}
                filterReset={filterBtnReset}
              />
            </>
          );
        })() : null}
        
        <ListItem className={classes.filterApply}>
          <Button size="small" fullWidth onClick={filterBtnApply}>{l('$global.buttons.apply')}</Button>
          <Button size="small" color="secondary" fullWidth onClick={filterBtnReset}>{l('$global.buttons.reset')}</Button>
        </ListItem>
      </Menu>
    </Paper>
  );
});
